import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { motion } from 'framer-motion';
import PurchaseCreditsModal from '../components/PurchaseCreditsModal';

const PricingPage = () => {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [creditPacks, setCreditPacks] = useState([
    { credits: 3, price: 6.00, popular: false },
    { credits: 5, price: 9.00, popular: false },
    { credits: 10, price: 16.00, popular: true }
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('monthly');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPurchaseOption, setSelectedPurchaseOption] = useState({
    type: 'credits', // 'credits' or 'subscription'
    id: null, // creditPack index or subscription id
    billingCycle: 'monthly' // 'monthly' or 'yearly'
  });

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { data, error } = await supabase
          .from('subscriptions')
          .select('*')
          .order('price_monthly');
          
        if (error) throw error;
        
        setSubscriptions(data || []);
      } catch (err) {
        console.error('Error fetching subscription data:', err);
        setError('Failed to load subscription information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptions();
  }, []);

  const handlePurchaseClick = (type, id, billingCycle = 'monthly') => {
    setSelectedPurchaseOption({
      type,
      id,
      billingCycle
    });
    setShowPurchaseModal(true);
  };

  const getSubscriptionPrice = (subscription) => {
    if (!subscription) return 0;
    return activeTab === 'monthly'
      ? subscription.price_monthly
      : subscription.price_yearly;
  };

  const getCostPerDocument = (subscription) => {
    if (activeTab === 'monthly') {
      return (subscription.price_monthly / subscription.usage_cap).toFixed(2);
    } else {
      // For yearly, divide by (usage_cap * 12) to get per-document cost
      return (subscription.price_yearly / (subscription.usage_cap * 12)).toFixed(2);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 py-20 text-center">
          <motion.h1 
            className="text-4xl md:text-5xl font-bold mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Simple, Transparent Pricing
          </motion.h1>
          <motion.p 
            className="text-xl md:text-2xl max-w-3xl mx-auto mb-8 text-blue-100"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Choose the plan that works best for your legal document needs
          </motion.p>
          
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 text-white p-4 rounded-lg mb-8 max-w-2xl mx-auto">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Subscription Plans Section */}
      <div className="container mx-auto px-4 py-16 -mt-10">
        <div className="max-w-5xl mx-auto">
          {/* Billing Toggle */}
          <div className="flex justify-center mb-12">
            <div className="bg-white p-1 rounded-full shadow-sm inline-flex">
              <button 
                onClick={() => setActiveTab('monthly')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeTab === 'monthly' 
                    ? 'bg-primary text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button 
                onClick={() => setActiveTab('yearly')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeTab === 'yearly' 
                    ? 'bg-primary text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Yearly <span className="text-xs font-bold text-green-500">Save 20%</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {subscriptions.map((sub) => (
                <motion.div 
                  key={sub.id}
                  className={`bg-white rounded-2xl shadow-lg overflow-hidden border ${
                    sub.tier_name === 'Pro' ? 'border-primary/30' : 'border-gray-200'
                  } flex flex-col`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className={`p-8 flex-grow ${sub.tier_name === 'Pro' ? 'bg-primary/5' : ''}`}>
                    <div className="flex items-center mb-3">
                      <h3 className="text-2xl font-bold text-gray-900">{sub.tier_name}</h3>
                      {sub.tier_name === 'Pro' && (
                        <div className="bg-blue-500 text-white text-xs font-bold py-1 px-3 rounded-full ml-2">
                          RECOMMENDED
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4 flex items-baseline">
                      <span className="text-4xl font-extrabold text-gray-900">
                        ${getSubscriptionPrice(sub)}
                      </span>
                      <span className="ml-1 text-xl font-medium text-gray-500">
                        /{activeTab === 'monthly' ? 'mo' : 'yr'}
                      </span>
                    </div>
                    
                    <p className="mt-2 text-gray-600">
                      ${getCostPerDocument(sub)} per document
                    </p>
                    
                    <p className="mt-2 text-gray-600">
                      {activeTab === 'monthly' 
                        ? `${sub.usage_cap} credits per month` 
                        : `${sub.usage_cap} credits per month`}
                    </p>
                  </div>
                  
                  <div className="px-8 pb-8 mt-auto">
                    <button 
                      onClick={() => handlePurchaseClick('subscription', sub.id, activeTab)}
                      className={`w-full py-3 px-4 rounded-xl font-medium text-center transition-all ${
                        sub.tier_name === 'Pro'
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:shadow-lg hover:shadow-indigo-500/30 hover:scale-105'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      {user ? 'Subscribe Now' : 'Sign Up & Subscribe'}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Divider with "or" */}
          <div className="flex items-center my-16">
            <div className="flex-grow border-t border-gray-300"></div>
            <span className="flex-shrink mx-4 text-gray-600 font-medium">OR BUY INDIVIDUAL CREDITS</span>
            <div className="flex-grow border-t border-gray-300"></div>
          </div>

          {/* Credit Packs */}
          <motion.div 
            className="grid md:grid-cols-3 gap-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {creditPacks.map((pack, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-xl shadow-sm overflow-hidden border relative ${
                  pack.popular ? 'border-blue-300' : 'border-gray-200'
                }`}
              >
                {pack.popular && (
                  <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold py-1 px-3 rounded-bl-lg">
                    POPULAR
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{pack.credits} Credits</h3>
                  <p className="text-3xl font-bold text-gray-900 mb-4">${pack.price.toFixed(2)}</p>
                  <p className="text-gray-600 mb-6">${(pack.price / pack.credits).toFixed(2)} per document</p>
                  
                  <button 
                    onClick={() => handlePurchaseClick('credits', index)}
                    className={`w-full py-2 px-4 rounded-lg font-medium text-center transition-all ${
                    pack.popular 
                      ? 'bg-primary text-white hover:bg-primary-dark'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}>
                    Purchase Credits
                  </button>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white py-16 mt-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            
            <div className="space-y-6">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">What counts as one document credit?</h3>
                <p className="text-gray-700">Each time you generate a new legal document, one credit is used. You can edit and download the document multiple times without using additional credits.</p>
              </div>
              
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Do my credits expire?</h3>
                <p className="text-gray-700">Credits from subscription plans reset every billing period. Individually purchased credits never expire and remain in your account until used.</p>
              </div>
              
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I change my subscription plan?</h3>
                <p className="text-gray-700">Yes, you can upgrade, downgrade, or cancel your subscription at any time. Changes will take effect at the start of your next billing cycle.</p>
              </div>
              
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">What happens if I run out of credits?</h3>
                <p className="text-gray-700">You can purchase additional credits at any time, regardless of whether you have an active subscription or not.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to get started?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Create your first legal document in minutes with our AI-powered platform.
          </p>
          <Link 
            to={user ? "/dashboard" : "/signup"} 
            className="inline-block bg-white text-indigo-600 px-8 py-3 rounded-lg font-medium text-lg hover:shadow-lg transition-all"
          >
            {user ? 'Go to Dashboard' : 'Sign Up Now'}
          </Link>
        </div>
      </div>

      {/* Purchase Modal */}
      <PurchaseCreditsModal 
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        initialOption={selectedPurchaseOption.type}
        selectedCredits={selectedPurchaseOption.type === 'credits' ? 
          creditPacks[selectedPurchaseOption.id]?.credits || 10 : undefined}
        selectedSubscription={selectedPurchaseOption.type === 'subscription' ? 
          selectedPurchaseOption.id : undefined}
        initialBillingCycle={selectedPurchaseOption.billingCycle}
      />
    </div>
  );
};

export default PricingPage;